import React, { useEffect, useState } from 'react'
import styles from 'styles/components/StripePaymentForm.module.sass'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import StripeError from 'interface/StripeError'

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!publishableKey) {
    throw new Error('Stripe publishable key is not set.')
}

interface SubscriptionUpgradeFormProps {
    paymentIntentClientSecret: string;
    setLoading: (loading: boolean) => void;
    handleProcessSubscriptionResponse: ({ status, errorMessage, paymentId } : { status: 'succeeded' | 'failed' | 'canceled'; errorMessage?: string; paymentId?: string }) => void;
}

const extractStripeErrorMessage = (error: StripeError): string => {
    return (
        String(error?.payment_intent?.last_payment_error?.message) ||
        String(error?.message) ||
        String(error?.code) ||
        String(error?.decline_code) ||
        'Payment failed. Please contact support if the issue persists.'
    )
}

/**
 * SubscriptionUpgradeForm component for upgrading a subscription.
 * @component
 * 
 * @param param0 Props for the SubscriptionUpgradeForm component.
 * @param param0.paymentIntentClientSecret The client secret for the Payment Intent from the server.
 * @param param0.setLoading Function to set loading state in the parent component.
 * @returns {JSX.Element} SubscriptionUpgradeForm component
 * 
 * @example
 * // Example usage:
 * <SubscriptionUpgradeForm
 *   paymentIntentClientSecret="pi_1HxxxxxxSecretKey"
 *   setLoading={(loading) => console.log("Loading:", loading)}
 *   handleProcessSubscriptionResponse={({ status, errorMessage, paymentId }) => {
 *      console.log("Subscription process result:", status, errorMessage, paymentId);
 *   }}
 * />
 */
const SubscriptionUpgradeForm: React.FC<SubscriptionUpgradeFormProps> = ({ paymentIntentClientSecret, setLoading, handleProcessSubscriptionResponse }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [needsCard, setNeedsCard] = useState(false);

    // Try to confirm the payment intent without collecting new card details first.
    useEffect(() => {
        const tryAutoConfirmPayment = async () => {
            if (!stripe || !paymentIntentClientSecret) return;

            console.log ('Attempting to confirm payment intent automatically...');

            setIsSubmitting(true);
            setLoading(true);

            const { error, paymentIntent } = await stripe.confirmCardPayment(paymentIntentClientSecret);

            setIsSubmitting(false);
            setLoading(false);

            if (paymentIntent?.status === 'succeeded') {
                handleProcessSubscriptionResponse({ status: 'succeeded', paymentId: paymentIntent.id })
                return;
            }

            const requiresPaymentMethod = 
                paymentIntent?.status === 'requires_payment_method' ||
                (error && (error as any)?.code === 'requires_payment_method') ||
                (error && (error as any)?.payment_intent.status === 'requires_payment_method');

            const needsAuthentication =
                paymentIntent?.status === 'requires_action' ||
                (error && (error as any)?.code === 'payment_intent_authentication_failure') ||
                (error && (error as any)?.payment_intent.status === 'requires_action');

            if (requiresPaymentMethod || needsAuthentication) {
                // Payment method is required or authentication is needed, show the card input form.
                setNeedsCard(true);
                return;
            }

            // If nothing matched but it's not succeeded, prompt for card details.
            setNeedsCard(true);
        };

        void tryAutoConfirmPayment();
    }, [stripe, paymentIntentClientSecret]);

    // Handle form submission when card details are provided.
    const handleSubmitWithCard = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return handleProcessSubscriptionResponse({ status: 'failed', errorMessage: 'Stripe has not loaded yet.' });
        
        const card = elements.getElement(CardElement);
        if (!card) return handleProcessSubscriptionResponse({ status: 'failed', errorMessage: 'Payment form is not ready yet.' });

        setIsSubmitting(true);
        setLoading(true);

        try {
            const { error, paymentIntent } = await stripe.confirmCardPayment(paymentIntentClientSecret, {
                payment_method: { card },
                setup_future_usage: 'off_session',
            });

            if (error) {

                const stripeError = error as StripeError;
                const detailedMessage = extractStripeErrorMessage(stripeError);

                handleProcessSubscriptionResponse({ status: 'failed', errorMessage: detailedMessage });
                return;
            }
            if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                handleProcessSubscriptionResponse({ status: 'failed', errorMessage: `Payment not completed. Status: ${paymentIntent?.status}` });
                return;
            }

            // Payment succeeded. Notify parent component.
            handleProcessSubscriptionResponse({ status: 'succeeded', paymentId: paymentIntent.id });
        } finally {
            setIsSubmitting(false);
            setLoading(false);
        }
    }

    const cardElementOptions = {
        style: {
            base: {
                width: '100%',
                height: 'auto',
                minHeight: 'auto',
                fontSize: '18px',
                color: '#424770',
                letterSpacing: '0.025em',
                fontFamily: 'Poppins, sans-serif',
                padding: '10px',
                '::placeholder': {
                    color: '#aab7c4',
                },
            },
            invalid: {
                color: '#9e2146',
            },
        },
    };

    if (!needsCard) {
        return (
            <div className={styles['payment-form']}>
                <button
                    type="button"
                    disabled={true}
                    className={styles['submit-button']}
                >
                    {isSubmitting ? 'Confirming...' : 'Preparing Payment...'}
                </button>
            </div>
        );
    }

    const handleSubmitWrapper = (event: React.FormEvent) => {
        void handleSubmitWithCard(event)
    }

    return (
        <form onSubmit={handleSubmitWrapper} className={styles['payment-form']}>
            <div className={styles['card-element-container']}>
                <CardElement options={cardElementOptions} />
            </div>
            <button
                type="submit"
                disabled={!stripe || isSubmitting}
                className={styles['submit-button']}
            >
                {isSubmitting ? 'Confirming...' : 'Pay Now'}
            </button>
            {/**Cancel option */}
            <button
                type="button"
                className={styles['cancel-button']}
                onClick={() => handleProcessSubscriptionResponse({ status: 'canceled' })}
                disabled={isSubmitting}
            >
                Cancel
            </button>
        </form>
    );
}

export default SubscriptionUpgradeForm;